import { createContext, FunctionComponent } from 'preact';
import { useContext, useEffect, useMemo, useState } from 'preact/hooks';

import { useCurrentAsset } from '../CurrentAssetProvider';
import {
  getAssetAccess,
  isSportAccessLevel,
} from '../../../../utils/player/asset';
import { usePaywallState } from '../usePaywallState';
import { useProvider } from '../useProvider';

import { SportUpcomingEventTransformed } from './types';
import { fetchUpcomingSportEvents, isSupportedAssetProvider } from './helpers';

const SportUpcomingEventsContext =
  createContext<SportUpcomingEventsContextType>({
    state: 'loading',
    events: [],
    enabled: false,
  });

type SportUpcomingEventsContextType = {
  state: 'loading' | 'error' | 'done';
  events: SportUpcomingEventTransformed[];
  enabled: boolean;
};

export const SportUpcomingEventsProvider: FunctionComponent = ({
  children,
}) => {
  const [events, setState] = useState<{
    state: SportUpcomingEventsContextType['state'];
    events: SportUpcomingEventTransformed[];
  }>({
    state: 'loading',
    events: [],
  });
  const { asset } = useCurrentAsset();
  const accessLevel = getAssetAccess(asset);
  const { hasAccess } = usePaywallState(accessLevel);
  const provider = useProvider();

  const shouldFetchEvents =
    isSupportedAssetProvider(provider.assetProvider) &&
    !hasAccess &&
    isSportAccessLevel(provider.configProvider, accessLevel);

  useEffect(() => {
    if (shouldFetchEvents && isSupportedAssetProvider(provider.assetProvider)) {
      fetchUpcomingSportEvents(provider.assetProvider)
        .then((data) => {
          setState({
            state: 'done',
            events: data,
          });
        })
        .catch((error) => {
          console.error(error);
          setState({ state: 'error', events: [] });
        });
    } else {
      setState({ state: 'done', events: [] });
    }
  }, [provider.assetProvider, shouldFetchEvents]);

  const value = useMemo(
    () => ({ ...events, enabled: shouldFetchEvents }),
    [events, shouldFetchEvents]
  );

  return (
    <SportUpcomingEventsContext.Provider value={value}>
      {children}
    </SportUpcomingEventsContext.Provider>
  );
};

export const useSportUpcomingEvents = (): SportUpcomingEventsContextType => {
  const events = useContext(SportUpcomingEventsContext);

  if (process.env.NODE_ENV !== 'production' && !events) {
    console.warn(
      'SportUpcomingEvents context is undefined, please verify you are calling useSportUpcomingEvents() as child of a <SportUpcomingEventsProvider> component.'
    );
  }

  return events;
};
